import { type ReactNode, useEffect, useRef, useState } from "react";
import { Form as AntForm, type FormInstance } from "antd";
import { Button, Message, Spin, Tabs, useForm } from "@/frontend/components/common";
import { FaFutbol, FaPlus, FaTrophy, FaUsers } from "react-icons/fa";
import { tournamentFormatService, tournamentStandingService } from "@/frontend/services";
import type { TournamentFormatResponseDTO } from "@/shared/dto";
import { StageType, type StageConfig } from "@/core/value-objects/StageConfig";
import { StageFormItem } from "@/frontend/pages/tournament-formats/components/StageFormItem";

const QUICK_STAGE_OPTIONS = [
  {
    label: "Grupos",
    stage: { type: StageType.GROUPS, name: "Fase de Grupos" },
  },
  {
    label: "Dieciseisavos",
    stage: {
      type: StageType.KNOCKOUT,
      name: "Dieciseisavos de Final",
      knockoutRound: "ROUND_OF_32",
      teamsCount: 32,
      matchesCount: 16,
    },
  },
  {
    label: "Octavos",
    stage: {
      type: StageType.KNOCKOUT,
      name: "Octavos de Final",
      knockoutRound: "ROUND_OF_16",
      teamsCount: 16,
      matchesCount: 8,
    },
  },
  {
    label: "Cuartos",
    stage: {
      type: StageType.KNOCKOUT,
      name: "Cuartos de Final",
      knockoutRound: "QUARTER_FINAL",
      teamsCount: 8,
      matchesCount: 4,
    },
  },
  {
    label: "Semifinal",
    stage: {
      type: StageType.KNOCKOUT,
      name: "Semifinal",
      knockoutRound: "SEMI_FINAL",
      teamsCount: 4,
      matchesCount: 2,
    },
  },
  {
    label: "Final",
    stage: {
      type: StageType.KNOCKOUT,
      name: "Final",
      knockoutRound: "FINAL",
      teamsCount: 2,
      matchesCount: 1,
    },
  },
  {
    label: "3er lugar",
    stage: {
      type: StageType.KNOCKOUT,
      name: "Tercer Lugar",
      knockoutRound: "THIRD_PLACE",
      teamsCount: 2,
      matchesCount: 1,
      isConsolation: true,
    },
  },
];

interface StagesEditorProps {
  formatId: string;
  seasonId: string;
  onStagesChange?: (stages: StageConfig[]) => void;
  renderGroupAssignment?: (
    stage: StageConfig,
    onManualSetupChange?: (setup: ManualGroupSetupDraft) => void,
  ) => ReactNode;
  renderKnockoutAssignment?: (stage: StageConfig) => ReactNode;
}

interface ManualGroupSetupDraft {
  type: "GROUPS";
  stageOrder?: number;
  teamsPerGroup: number;
  groups: Array<{ id: string; name: string }>;
  assignments: Record<string, string | null>;
}

interface StageFormItemRowProps {
  form: FormInstance;
  fieldName: number;
  getManualGroupSetupHandler: (
    stageOrder: number,
  ) => (setup: ManualGroupSetupDraft) => void;
  renderGroupAssignment?: (
    stage: StageConfig,
    onManualSetupChange?: (setup: ManualGroupSetupDraft) => void,
  ) => ReactNode;
  renderKnockoutAssignment?: (stage: StageConfig) => ReactNode;
}

// Componente propio por fila: necesario para que useWatch respete las reglas
// de hooks cuando se agregan/quitan fases (el número de filas cambia).
function StageFormItemRow({
  form,
  fieldName,
  getManualGroupSetupHandler,
  renderGroupAssignment,
  renderKnockoutAssignment,
}: StageFormItemRowProps) {
  const stageType = AntForm.useWatch(["stages", fieldName, "type"], form);
  const stage = AntForm.useWatch(["stages", fieldName], form) as StageConfig | undefined;
  const effectiveStageType = stage?.type ?? stageType ?? StageType.GROUPS;
  const stageWithOrder = stage ? { ...stage, type: effectiveStageType, order: fieldName + 1 } : undefined;

  return (
    <StageFormItem
      fieldName={fieldName}
      stageType={effectiveStageType}
      groupAssignmentNode={
        effectiveStageType === StageType.GROUPS && stageWithOrder
          ? renderGroupAssignment?.(
              { ...stageWithOrder, type: StageType.GROUPS },
              getManualGroupSetupHandler(stageWithOrder.order),
            )
          : undefined
      }
      knockoutAssignmentNode={
        (effectiveStageType === StageType.KNOCKOUT || effectiveStageType === StageType.PLAYOFF) &&
        stageWithOrder
          ? renderKnockoutAssignment?.(stageWithOrder)
          : undefined
      }
    />
  );
}

// Etiqueta de cada tab: muestra el nombre de la fase (en vivo) con un ícono
// según el tipo, y "Fase N" como respaldo mientras no se le ha puesto nombre.
function StageTabLabel({
  form,
  fieldName,
  stageIndex,
}: {
  form: FormInstance;
  fieldName: number;
  stageIndex: number;
}) {
  const name = AntForm.useWatch(["stages", fieldName, "name"], form);
  const type = AntForm.useWatch(["stages", fieldName, "type"], form);
  const Icon = type === StageType.GROUPS ? FaUsers : type === StageType.PLAYOFF ? FaFutbol : FaTrophy;

  return (
    <span className="flex items-center gap-2">
      <Icon className={type === StageType.GROUPS ? "text-blue-500" : "text-orange-500"} />
      {name?.trim() ? name : `Fase ${stageIndex + 1}`}
    </span>
  );
}

function isQuickOptionAlreadyAdded(
  option: (typeof QUICK_STAGE_OPTIONS)[number],
  stages: Partial<StageConfig>[]
): boolean {
  return stages.some((s) => {
    if (!s || s.type !== option.stage.type) return false;
    if ("knockoutRound" in option.stage) {
      return s.structure?.knockoutRound === option.stage.knockoutRound;
    }
    return true;
  });
}

function makeBlankStage(
  order: number,
  preset?: (typeof QUICK_STAGE_OPTIONS)[number]["stage"]
): Partial<StageConfig> {
  const type = preset?.type ?? StageType.GROUPS;
  const isKnockout = type === StageType.KNOCKOUT || type === StageType.PLAYOFF;

  return {
    type,
    order,
    name: preset?.name ?? "",
    rules: { pointsPerWin: 3, pointsPerDraw: 1, pointsPerLoss: 0, tieBreakers: [] },
    structure: {
      isDoubleLeg: false,
      hasExtraTime: isKnockout,
      hasPenalties: isKnockout,
      knockoutRound: preset && "knockoutRound" in preset ? preset.knockoutRound : undefined,
      knockoutTeamsCount: preset && "teamsCount" in preset ? preset.teamsCount : undefined,
      knockoutMatchesCount: preset && "matchesCount" in preset ? preset.matchesCount : undefined,
    },
    progression: {
      promotedCount: preset && "teamsCount" in preset ? Math.max(preset.teamsCount / 2, 1) : 0,
    },
    isConsolation: preset && "isConsolation" in preset ? preset.isConsolation : false,
  };
}

function getSavedManualGroupSetups(
  config: TournamentFormatResponseDTO["config"],
): Record<string, ManualGroupSetupDraft> {
  const setups =
    (config?.manualGroupSetups as Record<string, ManualGroupSetupDraft> | undefined) ??
    {};
  const legacySetup = config?.manualGroupSetup as ManualGroupSetupDraft | undefined;

  if (legacySetup && !setups[String(legacySetup.stageOrder ?? 1)]) {
    return {
      ...setups,
      [String(legacySetup.stageOrder ?? 1)]: legacySetup,
    };
  }

  return setups;
}

function buildGroupStageFromSetup(
  setup: ManualGroupSetupDraft,
  order: number,
): StageConfig {
  return {
    type: StageType.GROUPS,
    order,
    name: order === 1 ? "Fase de Grupos" : `Fase de Grupos ${order}`,
    rules: {
      pointsPerWin: 3,
      pointsPerDraw: 1,
      pointsPerLoss: 0,
      tieBreakers: [],
    },
    structure: {
      groupsCount: setup.groups.length,
      teamsPerGroup: setup.teamsPerGroup,
      isDoubleLeg: false,
      hasExtraTime: false,
      hasPenalties: false,
    },
    progression: {
      promotedCount: 0,
    },
  };
}

function resolveSavedStages(
  config: TournamentFormatResponseDTO["config"],
): StageConfig[] {
  const stages = [...(config?.stages ?? [])].sort((a, b) => a.order - b.order);
  const manualSetups = getSavedManualGroupSetups(config);
  const missingGroupStages = Object.entries(manualSetups)
    .map(([orderText, setup]) => ({
      order: Number(orderText) || setup.stageOrder || 1,
      setup,
    }))
    .filter(
      ({ order }) =>
        !stages.some((stage) => stage.type === StageType.GROUPS && stage.order === order),
    )
    .map(({ order, setup }) => buildGroupStageFromSetup(setup, order));

  return [...stages, ...missingGroupStages].sort((a, b) => a.order - b.order);
}

export function StagesEditor({
  formatId,
  seasonId,
  onStagesChange,
  renderGroupAssignment,
  renderKnockoutAssignment,
}: StagesEditorProps) {
  const [form] = useForm();
  const currentStages = (AntForm.useWatch("stages", form) ?? []) as Partial<StageConfig>[];
  const [format, setFormat] = useState<TournamentFormatResponseDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const originalStagesRef = useRef<StageConfig[]>([]);
  const [activeStageKey, setActiveStageKey] = useState<string>();
  const [manualGroupSetupDrafts, setManualGroupSetupDrafts] = useState<
    Record<string, ManualGroupSetupDraft>
  >({});

  const handleManualGroupSetupChange = (
    stageOrder: number,
    setup: ManualGroupSetupDraft,
  ) => {
    setManualGroupSetupDrafts((prev) => ({
      ...prev,
      [String(stageOrder)]: setup,
    }));
  };

  const manualGroupSetupHandlersRef = useRef<
    Record<string, (setup: ManualGroupSetupDraft) => void>
  >({});

  const getManualGroupSetupHandler = (stageOrder: number) => {
    const key = String(stageOrder);
    manualGroupSetupHandlersRef.current[key] ??= (setup) => {
      handleManualGroupSetupChange(stageOrder, setup);
    };
    return manualGroupSetupHandlersRef.current[key];
  };

  useEffect(() => {
    let active = true;

    async function fetchFormat() {
      setLoading(true);
      try {
        const res = await tournamentFormatService.getById(formatId);
        if (!active) return;

        if (res.success && res.data) {
          setFormat(res.data);
          setManualGroupSetupDrafts({});
          const stages = resolveSavedStages(res.data.config);
          originalStagesRef.current = stages;
          form.setFieldsValue({ stages });
          setActiveStageKey(stages.length > 0 ? "0" : undefined);
          onStagesChange?.(stages);
        } else {
          Message.error({ content: res.error ?? "No se pudo cargar el formato del torneo" });
        }
      } catch (err) {
        Message.error({
          content: err instanceof Error ? err.message : "No se pudo cargar el formato del torneo",
        });
      } finally {
        if (active) setLoading(false);
      }
    }

    fetchFormat();
    return () => {
      active = false;
    };
  }, [formatId, form, onStagesChange]);

  const handleSubmit = async (values: { stages: Partial<StageConfig>[] }) => {
    if (!format) return;

    setSaving(true);
    try {
      const latestFormatRes = await tournamentFormatService.getById(formatId);
      if (!latestFormatRes.success || !latestFormatRes.data) {
        throw new Error(latestFormatRes.error ?? "No se pudo cargar el formato más reciente");
      }

      const latestFormat = latestFormatRes.data;
      const original = resolveSavedStages(latestFormat.config);
      const mergedStages: StageConfig[] = (values.stages ?? []).map((edited, idx) => {
        const base = original[idx];
        const stageOrder = idx + 1;
        const stageType = (edited.type ?? base?.type ?? StageType.GROUPS) as StageType;
        const isElimination =
          stageType === StageType.KNOCKOUT || stageType === StageType.PLAYOFF;
        const manualGroupSetup = manualGroupSetupDrafts[String(stageOrder)];
        return {
          ...(base ?? {}),
          type: stageType,
          order: stageOrder,
          name: edited.name ?? "",
          rules: {
            pointsPerWin: edited.rules?.pointsPerWin ?? base?.rules?.pointsPerWin ?? 0,
            pointsPerDraw: edited.rules?.pointsPerDraw ?? base?.rules?.pointsPerDraw ?? 0,
            pointsPerLoss: edited.rules?.pointsPerLoss ?? base?.rules?.pointsPerLoss ?? 0,
            tieBreakers: edited.rules?.tieBreakers ?? base?.rules?.tieBreakers ?? [],
          },
          structure: {
            ...(base?.structure ?? {}),
            ...(edited.structure ?? {}),
            isDoubleLeg:
              edited.structure?.isDoubleLeg ??
              base?.structure?.isDoubleLeg ??
              false,
            hasExtraTime:
              edited.structure?.hasExtraTime ??
              base?.structure?.hasExtraTime ??
              isElimination,
            hasPenalties:
              edited.structure?.hasPenalties ??
              base?.structure?.hasPenalties ??
              isElimination,
            knockoutRound:
              edited.structure?.knockoutRound ??
              base?.structure?.knockoutRound ??
              undefined,
            knockoutTeamsCount:
              edited.structure?.knockoutTeamsCount ??
              base?.structure?.knockoutTeamsCount ??
              undefined,
            knockoutMatchesCount:
              edited.structure?.knockoutMatchesCount ??
              base?.structure?.knockoutMatchesCount ??
              undefined,
            ...(manualGroupSetup
              ? {
                  groupsCount: manualGroupSetup.groups.length,
                  teamsPerGroup: manualGroupSetup.teamsPerGroup,
                }
              : {}),
          },
          progression: {
            ...(base?.progression ?? {}),
            ...(edited.progression ?? {}),
            promotedCount:
              edited.progression?.promotedCount ??
              base?.progression?.promotedCount ??
              0,
          },
          isConsolation: edited.isConsolation ?? base?.isConsolation,
          // No editados por este formulario: se preservan tal cual venían.
          advantage: base?.advantage,
          mappings: base?.mappings,
        } as StageConfig;
      });

      const res = await tournamentFormatService.update(formatId, {
        name: latestFormat.name,
        sport: latestFormat.sport,
        config: {
          ...latestFormat.config,
          stages: mergedStages,
          manualGroupSetup:
            manualGroupSetupDrafts["1"] ??
            latestFormat.config?.manualGroupSetup,
          manualGroupSetups: {
            ...((latestFormat.config?.manualGroupSetups as
              | Record<string, ManualGroupSetupDraft>
              | undefined) ?? {}),
            ...manualGroupSetupDrafts,
          },
        },
      });

      if (res.success && res.data) {
        const syncResults = await Promise.all(
          Object.entries(manualGroupSetupDrafts).map(([stageOrder, setup]) =>
            tournamentStandingService.syncGroups({
              seasonId,
              stageOrder: Number(stageOrder) || setup.stageOrder || 1,
              stageName:
                mergedStages.find((stage) => stage.order === Number(stageOrder))
                  ?.name ?? "Fase de Grupos",
              teamsPerGroup: setup.teamsPerGroup,
              groups: setup.groups,
              assignments: setup.assignments,
            }),
          ),
        );
        const failedSync = syncResults.find((result) => !result.success);
        if (failedSync) {
          throw new Error(
            failedSync.error ?? "Formato guardado, pero no se pudieron sincronizar los grupos",
          );
        }

        Message.success({ content: "Formato actualizado correctamente" });
        setFormat(res.data);
        originalStagesRef.current = mergedStages;
        onStagesChange?.(mergedStages);
      } else {
        Message.error({ content: res.error ?? "Error al actualizar el formato" });
      }
    } catch (err) {
      Message.error({
        content: err instanceof Error ? err.message : "Error al actualizar el formato",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spin size="large" tip="Cargando formato..." />
      </div>
    );
  }

  if (!format) {
    return <p className="text-gray-500 py-4">No se pudo cargar el formato del torneo.</p>;
  }

  return (
    <AntForm form={form} onFinish={handleSubmit} layout="vertical" className="w-full">
      <AntForm.List name="stages">
        {(fields, { add, remove }) => {
          const addStage = (preset?: (typeof QUICK_STAGE_OPTIONS)[number]["stage"]) => {
            const newIndex = fields.length;
            add(makeBlankStage(newIndex + 1, preset));
            setActiveStageKey(String(newIndex));
          };

          const removeStage = (targetKey: string) => {
            const removedIndex = Number(targetKey);
            remove(removedIndex);

            setActiveStageKey((prevKey) => {
              if (prevKey == null) return prevKey;
              const prevIndex = Number(prevKey);
              if (prevIndex === removedIndex) {
                const remaining = fields.length - 1;
                return remaining > 0 ? String(Math.max(0, removedIndex - 1)) : undefined;
              }
              return prevIndex > removedIndex ? String(prevIndex - 1) : prevKey;
            });
          };

          return (
            <>
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-gray-700">
                    Agregar fases
                  </span>
                  <span className="text-xs text-gray-500">
                    Actual: {fields.length}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_STAGE_OPTIONS.map((option) => {
                    const alreadyAdded = isQuickOptionAlreadyAdded(option, currentStages);
                    return (
                      <Button
                        key={option.label}
                        size="small"
                        icon={<FaPlus />}
                        disabled={alreadyAdded}
                        title={alreadyAdded ? `Ya agregaste la fase de ${option.label}` : undefined}
                        onClick={() => addStage(option.stage)}
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                  <Button size="small" type="dashed" icon={<FaPlus />} onClick={() => addStage()}>
                    Fase en blanco
                  </Button>
                </div>
              </div>

              {fields.length === 0 ? (
                <p className="text-gray-500 text-sm py-6 text-center border border-dashed border-gray-300 rounded-lg mb-4">
                  No has agregado ninguna fase todavía. Usa los botones de arriba para empezar.
                </p>
              ) : (
                <Tabs
                  type="editable-card"
                  hideAdd
                  activeKey={activeStageKey}
                  onChange={(key) => setActiveStageKey(key)}
                  onEdit={(targetKey, action) => {
                    if (action === "remove") removeStage(targetKey as string);
                  }}
                  className="mb-4"
                  items={fields.map((field) => ({
                    key: String(field.name),
                    label: <StageTabLabel form={form} fieldName={field.name} stageIndex={field.name} />,
                    children: (
                      <StageFormItemRow
                        form={form}
                        fieldName={field.name}
                        getManualGroupSetupHandler={getManualGroupSetupHandler}
                        renderGroupAssignment={renderGroupAssignment}
                        renderKnockoutAssignment={renderKnockoutAssignment}
                      />
                    ),
                  }))}
                />
              )}
            </>
          );
        }}
      </AntForm.List>

      <div className="sticky bottom-4 z-30 flex justify-end border-t bg-white/90 pt-4 backdrop-blur">
        <Button type="primary" htmlType="submit" size="large" loading={saving}>
          Guardar Formato
        </Button>
      </div>
    </AntForm>
  );
}
